import { useState, useEffect, useCallback } from "react";
import { auth } from "../services/firebase";
import ChoirSongPerformance from "../DTOs/ChoirSongPerformance";
import { useTranslation } from "react-i18next";
import {
    addChoirSongPerformance,
    getAvailableChoirSongYears,
    getChoirSongPerformancesByYearGroupedByMonth,
    deleteChoirSongPerformance,
} from "../services/firestoreService";
import Papa from "papaparse";
import { handleFirebaseError } from "../utils/errorHandler";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import I_IMC_Props from "../infos/I_IMC_Props";
import {
    CSVImportSection,
    FilterControls,
    MonthlyTable,
    MonthlyPerformance,
    Repertoire,
} from "./choirsong";
import Spinner from "react-bootstrap/esm/Spinner";
import Card from "react-bootstrap/esm/Card";
import { logger } from "../utils/logger";
import Button from "react-bootstrap/esm/Button";

export default function ChoirSongManagement({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, loading: authLoading } = useAuth();
    const { activeGemeindeId } = useGemeinde();

    const getTranslationString = (translation: unknown): string =>
        typeof translation === "string" ? translation : "";

    // "months": performances by month; "repertoire": the song catalogue (GOD-18).
    const [view, setView] = useState<"months" | "repertoire">("months");
    const notify = useCallback((variant: string, message: string) => {
        setVariant(variant);
        setMessage(message);
        setShowMessage(true);
    }, [setVariant, setMessage, setShowMessage]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState<string[]>([]);
    const [showImportSection, setShowImportSection] = useState(false);
    const [expandedMonths, setExpandedMonths] = useState<Set<number>>(new Set());

    const [availableYears, setAvailableYears] = useState<number[]>([new Date().getFullYear()]);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState<number | null>(null);
    const [availableMonths, setAvailableMonths] = useState<number[]>([]);
    const [monthlyData, setMonthlyData] = useState<Map<number, MonthlyPerformance[]>>(new Map());
    const [searchTerm, setSearchTerm] = useState("");

    const getDateLocale = useCallback(() => (i18n.language.startsWith("de") ? "de-DE" : "en-US"), [i18n.language]);

    const formatDateForDisplay = useCallback((dateStr: string): string => {
        try {
            const locale = getDateLocale();
            const parsedDate = new Date(dateStr + "T00:00:00");
            return parsedDate.toLocaleDateString(locale, { 
                day: "numeric", 
                month: "long", 
                year: "numeric" 
            });
        } catch {
            return dateStr;
        }
    }, [getDateLocale]);

    // Load available years on mount
    useEffect(() => {
        if (authLoading || !isAuthenticated || !activeGemeindeId) return;

        async function loadYears() {
            if (!activeGemeindeId) return;
            try {
                const years = await getAvailableChoirSongYears(activeGemeindeId);
                if (years.length > 0) {
                    setAvailableYears(years);
                    // Set current year to most recent year with data
                    if (!years.includes(currentYear)) {
                        setCurrentYear(years[0]);
                    }
                }
            } catch (error) {
                logger.error("ChoirSongManagement.loadYears", error);
            }
        }
        loadYears();
    }, [isAuthenticated, authLoading, currentYear, activeGemeindeId]);

    // Load performances when year changes
    useEffect(() => {
        if (authLoading || !isAuthenticated || !activeGemeindeId) return;

        async function loadPerformances() {
            if (!activeGemeindeId) return;
            setLoading(true);
            try {
                const result = await getChoirSongPerformancesByYearGroupedByMonth(activeGemeindeId, currentYear);
                setMonthlyData(result.months);
                setAvailableMonths(result.availableMonths);
                // Expand the first month by default
                if (result.availableMonths.length > 0) {
                    setExpandedMonths(new Set([result.availableMonths[0]]));
                }
            } catch (error) {
                setVariant("danger");
                setMessage(handleFirebaseError(error, "loadChoirSongs"));
                setShowMessage(true);
            }
            setLoading(false);
        }
        loadPerformances();
    }, [isAuthenticated, authLoading, currentYear, activeGemeindeId, setVariant, setMessage, setShowMessage]);

    // Filter performances by search term
    const getFilteredData = useCallback((): Map<number, MonthlyPerformance[]> => {
        if (!searchTerm.trim()) return monthlyData;
        
        const term = searchTerm.toLowerCase();
        const filtered = new Map<number, MonthlyPerformance[]>();
        
        monthlyData.forEach((performances, month) => {
            const matchingPerformances = performances.filter(p => 
                p.songName.toLowerCase().includes(term)
            );
            if (matchingPerformances.length > 0) {
                filtered.set(month, matchingPerformances);
            }
        });
        
        return filtered;
    }, [monthlyData, searchTerm]);

    // Get months to display (filtered by currentMonth if set)
    const getDisplayMonths = useCallback((): number[] => {
        const filtered = getFilteredData();
        let months = Array.from(filtered.keys()).sort((a, b) => b - a);
        
        if (currentMonth !== null) {
            months = months.filter(m => m === currentMonth);
        }
        
        return months;
    }, [getFilteredData, currentMonth]);

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newYear = parseInt(e.target.value);
        setCurrentYear(newYear);
        setCurrentMonth(null);
        setExpandedMonths(new Set());
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        if (value === "") {
            setCurrentMonth(null);
        } else {
            const month = parseInt(value);
            setCurrentMonth(month);
            setExpandedMonths(new Set([month]));
        }
    };

    const toggleMonthExpanded = (month: number) => {
        setExpandedMonths(prev => {
            const next = new Set(prev);
            if (next.has(month)) {
                next.delete(month);
            } else {
                next.add(month);
            }
            return next;
        });
    };

    const expandAll = () => setExpandedMonths(new Set(getDisplayMonths()));
    const collapseAll = () => setExpandedMonths(new Set());

    async function handleDeletePerformance(songDocId: string, performanceDocId: string) {
        if (!activeGemeindeId) return;
        if (!window.confirm(getTranslationString(t("admin.deleteConfirm")))) return;

        setLoading(true);
        try {
            await deleteChoirSongPerformance(activeGemeindeId, songDocId, performanceDocId);

            setVariant("success");
            setMessage(getTranslationString(t("admin.performanceDeleted")));
            setShowMessage(true);

            // Reload data
            const result = await getChoirSongPerformancesByYearGroupedByMonth(activeGemeindeId, currentYear);
            setMonthlyData(result.months);
            setAvailableMonths(result.availableMonths);
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "deleteChoirSongPerformance"));
            setShowMessage(true);
        }
        setLoading(false);
    }

    async function handleCSVImport(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!activeGemeindeId) return;
        setImporting(true);
        setImportResults([]);

        const fileInput = e.currentTarget.CSVFile as HTMLInputElement;
        if (!fileInput.files || fileInput.files.length === 0) {
            setVariant("danger");
            setMessage(getTranslationString(t("admin.selectCSVFile")));
            setShowMessage(true);
            setImporting(false);
            return;
        }

        const file = fileInput.files[0];
        const text = await file.text();

        const parsed = Papa.parse(text, {
            delimiter: ",",
            skipEmptyLines: true,
            header: false,
        });

        if (parsed.errors.length > 0) {
            setVariant("danger");
            setMessage(
                `${getTranslationString(t("admin.csvParseError"))}: ${parsed.errors.map((e) => e.message).join(", ")}`,
            );
            setShowMessage(true);
            setImporting(false);
            return;
        }

        const lines = parsed.data as string[][];
        const results: string[] = [];
        let successCount = 0;
        let errorCount = 0;
        let skippedCount = 0;

        for (const columns of lines) {
            if (columns.length < 2) {
                results.push(`❌ ${t("admin.lineSkipped")}: ${columns.join(",")}`);
                errorCount++;
                continue;
            }

            let songName = columns[0].trim();
            let dataStartIndex = 1;

            for (let i = 1; i < columns.length; i++) {
                const col = columns[i].trim();
                if (!col) break;

                const looksLikeDate =
                    /^\d{1,2}\.\d{1,2}\./.test(col) || /^\d{4}-\d{1,2}/.test(col) || /^\d{1,2}\/\d{1,2}/.test(col);
                const isMarker = col === "X";

                if (looksLikeDate || isMarker) {
                    if (i > 1) dataStartIndex = i;
                    break;
                }

                songName = `${songName}, ${col}`;
                dataStartIndex = i + 1;
            }

            const dates = columns.slice(dataStartIndex).map((col) => col.trim());

            const hasCandidateDateOrMarker = dates.some((col) => {
                if (!col) return false;
                const looksLikeDate =
                    /^\d{1,2}\.\d{1,2}\./.test(col) || /^\d{4}-\d{1,2}/.test(col) || /^\d{1,2}\/\d{1,2}/.test(col);
                const isMarker = col === "X";
                return looksLikeDate || isMarker;
            });

            if (!hasCandidateDateOrMarker) {
                results.push(`⚠️ ${t("admin.lineSkipped")}: ${songName} (${t("admin.noValidDates")})`);
                skippedCount++;
                continue;
            }

            for (let dateStr of dates) {
                if (!dateStr) continue;

                if (dateStr === "X" || dateStr.length === 1) {
                    skippedCount++;
                    continue;
                }

                const originalDateStr = dateStr;
                dateStr = dateStr.split(" ")[0].trim();

                if (!dateStr) {
                    skippedCount++;
                    continue;
                }

                try {
                    let parsedDate: Date;
                    if (dateStr.includes(".")) {
                        const parts = dateStr.split(".");
                        if (parts.length === 3) {
                            let day = parseInt(parts[0]);
                            let month = parseInt(parts[1]);
                            let year = parseInt(parts[2]);

                            if (year < 100) {
                                year += year < 50 ? 2000 : 1900;
                            }

                            parsedDate = new Date(year, month - 1, day);
                        } else {
                            throw new Error("Invalid date format");
                        }
                    } else if (dateStr.includes("-")) {
                        parsedDate = new Date(dateStr + "T00:00:00");
                    } else if (dateStr.includes("/")) {
                        parsedDate = new Date(dateStr);
                    } else {
                        throw new Error("Unknown date format");
                    }

                    if (isNaN(parsedDate.getTime())) {
                        throw new Error("Invalid date");
                    }

                    const year = parsedDate.getFullYear();
                    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
                    const day = String(parsedDate.getDate()).padStart(2, "0");
                    const normalizedDate = `${year}-${month}-${day}`;

                    const performance = new ChoirSongPerformance(new Date(), auth.currentUser!.uid, normalizedDate, 1);

                    await addChoirSongPerformance(activeGemeindeId, songName, performance);

                    successCount++;
                    results.push(`✓ ${songName} - ${originalDateStr}`);
                } catch (error) {
                    const isDateParseError =
                        error instanceof Error &&
                        (error.message.toLowerCase().includes("invalid time value") ||
                            error.message.toLowerCase().includes("invalid date") ||
                            error.message.toLowerCase().includes("unknown date format"));

                    if (isDateParseError) {
                        skippedCount++;
                    } else {
                        handleFirebaseError(error, "csvImportPerformance");
                        errorCount++;
                        results.push(`❌ ${songName} - ${originalDateStr}: ${t("admin.importError")}`);
                    }
                }
            }
        }

        setImportResults(results);
        setVariant(errorCount === 0 && skippedCount === 0 ? "success" : "warning");
        setMessage(
            `${getTranslationString(t("admin.importCompleted", { successCount, errorCount }))}${
                skippedCount > 0 ? ` (${skippedCount} ${getTranslationString(t("admin.skippedEntries"))})` : ""
            }`,
        );
        setShowMessage(true);
        setImporting(false);

        // Reload data and years
        const years = await getAvailableChoirSongYears(activeGemeindeId);
        if (years.length > 0) {
            setAvailableYears(years);
        }
        const result = await getChoirSongPerformancesByYearGroupedByMonth(activeGemeindeId, currentYear);
        setMonthlyData(result.months);
        setAvailableMonths(result.availableMonths);
        (document.getElementById("CSVImportForm") as HTMLFormElement).reset();
    }

    const filteredData = getFilteredData();
    const displayMonths = getDisplayMonths();
    const totalPerformances = Array.from(filteredData.values()).reduce((acc, perfs) => acc + perfs.length, 0);

    return (
        <div className="mx-auto" style={{ maxWidth: "1200px" }}>
            {/* Header with Stats */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 className="mb-1" style={{ fontWeight: 600 }}>
                        🎵 {t("admin.choirSongManagement")}
                    </h2>
                    {view === "months" && (
                        <p className="text-muted mb-0">
                            {t("admin.performanceSummary", { performances: totalPerformances, count: displayMonths.length })}
                        </p>
                    )}
                </div>
                {view === "months" && <CSVImportSection
                    showImportSection={showImportSection}
                    setShowImportSection={setShowImportSection}
                    importing={importing}
                    importResults={importResults}
                    onCSVImport={handleCSVImport}
                />}
            </div>

            <div className="mb-4 d-flex gap-2">
                <Button variant={view === "months" ? "primary" : "outline-primary"} onClick={() => setView("months")}>
                    {t("repertoire.viewMonths")}
                </Button>
                <Button variant={view === "repertoire" ? "primary" : "outline-primary"} onClick={() => setView("repertoire")}>
                    {t("repertoire.viewRepertoire")}
                </Button>
            </div>

            {view === "repertoire" && activeGemeindeId && <Repertoire gemeindeId={activeGemeindeId} notify={notify} />}

            {view === "months" && <>

            <FilterControls
                currentYear={currentYear}
                yearOptions={availableYears}
                currentMonth={currentMonth}
                availableMonths={availableMonths}
                searchTerm={searchTerm}
                onYearChange={handleYearChange}
                onMonthChange={handleMonthChange}
                onSearchChange={setSearchTerm}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
            />

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">{t("common.loading")}</p>
                </div>
            ) : displayMonths.length === 0 ? (
                <Card className="text-center py-5 shadow-sm border-0" style={{ backgroundColor: "var(--gl-surface-raised)" }}>
                    <Card.Body>
                        <div style={{ fontSize: "3rem" }}>🎵</div>
                        <h5 className="mt-3 text-muted">
                            {searchTerm ? t("admin.noSongsFound") : t("admin.noSongsThisYear")}
                        </h5>
                        {searchTerm && (
                            <Button variant="link" onClick={() => setSearchTerm("")}>
                                {t("admin.resetSearch")}
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                displayMonths.map(month => (
                    <MonthlyTable
                        key={month}
                        month={month}
                        year={currentYear}
                        performances={filteredData.get(month) || []}
                        isExpanded={expandedMonths.has(month)}
                        onToggleExpanded={() => toggleMonthExpanded(month)}
                        onDeletePerformance={handleDeletePerformance}
                        formatDateForDisplay={formatDateForDisplay}
                    />
                ))
            )}
            </>}
        </div>
    );
}
